// Device Fingerprinting & GPS Capture Service
// Uses FingerprintJS Pro for browser fingerprinting
// Captures GPS coordinates with user permission

/**
 * Capture device fingerprint and GPS coordinates
 * @returns {Promise<object>} - { fingerprint, gps: { latitude, longitude }, timestamp, error }
 */
async function captureDeviceFingerprintAndGPS() {
    const result = {
        fingerprint: null,
        gps: {
            latitude: null,
            longitude: null
        },
        timestamp: new Date().toISOString(),
        error: null
    };

    try {
        // 1. Capture Device Fingerprint using FingerprintJS
        console.log('📱 Capturing device fingerprint...');

        // Load FingerprintJS library
        const FingerprintJS = await import('https://cdn.jsdelivr.net/npm/@fingerprintjs/fingerprintjs@4/dist/fp.esm.min.mjs');

        // Initialize fingerprinting
        const fp = await FingerprintJS.load();

        // Get fingerprint
        const fingerprintResult = await fp.get();

        // Store full fingerprint data as string (includes all components)
        result.fingerprint = JSON.stringify({
            visitorId: fingerprintResult.visitorId,
            confidence: fingerprintResult.confidence,
            components: fingerprintResult.components
        });

        console.log('✅ Fingerprint captured:', fingerprintResult.visitorId);

    } catch (error) {
        console.error('❌ Fingerprint capture failed:', error);
        result.error = `Fingerprint error: ${error.message}`;
    }

    try {
        // 2. Capture GPS Coordinates
        console.log('📍 Requesting GPS coordinates...');

        if (!navigator.geolocation) {
            throw new Error('Geolocation not supported by this browser');
        }

        // Get current position
        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,  // 10 second timeout
                maximumAge: 0     // Don't use cached position
            });
        });

        result.gps.latitude = position.coords.latitude;
        result.gps.longitude = position.coords.longitude;

        console.log('✅ GPS captured:', result.gps);

    } catch (error) {
        console.warn('⚠️ GPS capture failed:', error.message);
        // GPS failure is not critical - we can still use fingerprint
        if (result.error) {
            result.error += ` | GPS error: ${error.message}`;
        } else {
            result.error = `GPS error: ${error.message}`;
        }
    }

    return result;
}

/**
 * Capture just the fingerprint (no GPS)
 *
 * OPTIMIZATION: Uses pre-loaded FingerprintJS instance if available
 * Saves ~500ms by reusing already-loaded library
 *
 * @returns {Promise<string|null>} - Fingerprint string or null
 */
async function captureFingerprint() {
    try {
        console.log('📱 Capturing device fingerprint...');

        let fp;

        // OPTIMIZATION: Use pre-loaded instance if available (saves ~500ms)
        if (window.__fpInstance) {
            console.log('[Optimization] Using pre-loaded FingerprintJS instance');
            fp = window.__fpInstance;
        } else {
            // Fallback: Load FingerprintJS on demand
            console.log('[Optimization] Loading FingerprintJS on demand');
            const FingerprintJS = await import('https://cdn.jsdelivr.net/npm/@fingerprintjs/fingerprintjs@4/dist/fp.esm.min.mjs');
            fp = await FingerprintJS.load();
        }

        const result = await fp.get();

        const fingerprintData = JSON.stringify({
            visitorId: result.visitorId,
            confidence: result.confidence,
            components: result.components
        });

        console.log('✅ Fingerprint captured:', result.visitorId);
        return fingerprintData;

    } catch (error) {
        console.error('❌ Fingerprint capture failed:', error);
        return null;
    }
}

/**
 * Capture just GPS (no fingerprint)
 * @returns {Promise<object|null>} - { latitude, longitude } or null
 */
async function captureGPS() {
    try {
        console.log('📍 Requesting GPS coordinates...');

        if (!navigator.geolocation) {
            throw new Error('Geolocation not supported');
        }

        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });
        });

        const gps = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
        };

        console.log('✅ GPS captured:', gps);
        return gps;

    } catch (error) {
        console.warn('⚠️ GPS capture failed:', error.message);

        // Show user-friendly message based on error
        if (error.code === 1) {
            console.log('📍 Location permission denied - continuing without GPS');
            showGPSNotice('Location access was denied. For enhanced security, please enable location services in your browser settings.');
        } else if (error.code === 2) {
            console.log('📍 Location unavailable - continuing without GPS');
        } else if (error.code === 3) {
            console.log('📍 Location request timed out - continuing without GPS');
        }

        return null;
    }
}

/**
 * Show GPS notice to user (non-blocking)
 * @param {string} message - Message to display
 */
function showGPSNotice(message) {
    // Create a temporary notice element
    const notice = document.createElement('div');
    notice.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #fff3cd;
        color: #856404;
        padding: 12px 20px;
        border-radius: 8px;
        border: 1px solid #ffc107;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 90%;
        text-align: center;
        font-size: 14px;
        animation: slideDown 0.3s ease-out;
    `;
    notice.innerHTML = `
        <strong>ℹ️ GPS Optional</strong><br>
        ${message}
    `;

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
        @keyframes slideDown {
            from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notice);

    // Auto-remove after 5 seconds
    setTimeout(() => {
        notice.style.transition = 'opacity 0.3s';
        notice.style.opacity = '0';
        setTimeout(() => notice.remove(), 300);
    }, 5000);
}

// Export functions for use in other scripts
// Browser global export (for inline scripts)
if (typeof window !== 'undefined') {
    window.captureDeviceFingerprintAndGPS = captureDeviceFingerprintAndGPS;
    window.captureFingerprint = captureFingerprint;
    window.captureGPS = captureGPS;
}

// Node.js module export (for bundling)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        captureDeviceFingerprintAndGPS,
        captureFingerprint,
        captureGPS
    };
}
